import React, { useContext, useCallback, ReactElement, useRef } from 'react';
import { useNavigate, useLocation, useParams } from "react-router";

import { useEffect, useState } from 'react';
import { ToolbarContext } from '../../contexts/toolbar-context';
import { useUserContext } from '../../contexts/user-context';
import { EContractDialog } from './panes/EContractDialog';
import { ExchangeRatesContextProvider } from '../../contexts/exchange-rates-context';
import { Box, Tab, Tabs, useTheme } from "@mui/material";
import { Outlet } from "react-router-dom";
import WaitSpinner from "../../components/wait-spinner";
import { Campaign } from "../../generated/graphql";
import { BookmarkAdd, BookmarkRemove } from "@mui/icons-material";
import { Icon } from '@iconify/react';
import { useToast } from "../../contexts/toast";
import { useDaybookContext } from './../../contexts/Daybook/DayBookContext';
import GQLChanges from '../../utils/graphql-changes';
import { fieldMappings } from './fieldMappings';
//import { ChallengeEmailDialog } from './ChallengeEmail';

// todo change ERR to be a true object rather than just strings (also requires changing fieldtable)
/*
function tabLabel(label, obj, err) {
    return (obj && obj.length) ? <span className={err ? Classes.INTENT_DANGER : ''}>{label}</span> : <span className="tab-empty">{label}</span>;
}
*/
type tabStatusArgs = {
    label: string,
    hasContent: boolean,
    value: string,
    error: boolean,
    changeTab: any
};

export type CampaignOutletContextValue = {
    initialValues: Campaign
    setTab: (tab: string) => void
    isDirty: boolean
    onChange: any
    setValue: any
    setTransient: any
    transientValues: any
    values: Campaign
    readOnly: boolean
    inProductionLocked: boolean
    isReadOnly: (hasPriceImpact: boolean) => boolean
    loading: boolean
    jobRef: string
    errors: any
    updateInitialValues: (values: any, name?: any) => void
}

// A creative-studio campaign in the `In Production` status locks the whole editor for
// users who lack the in-production edit permission. The lock is derived from the status
// and the campaign's type (not the is_locked column, which has its own semantics).
export const isInProductionLocked = (status?: string | null, type?: string | null, hasInProductionEdit?: boolean): boolean =>
    status === 'In Production' && type === 'creative_studio' && !hasInProductionEdit;

/*
export function CampaignForm({diaForm,tabErrors}) {
    console.log('RENDERING WITH',diaForm);
    return <CampaignFormContent diaForm={diaForm} tabErrors={tabErrors}/>;
}
*/
function TabStatus(props: tabStatusArgs): ReactElement {
    const theme = useTheme();
    /*    let status = (palette)=>palette.text.disabled;
        let typo = (typography)=>typography.fontWeightLight;
        if(props.hasContent) { 
            status = (palette)=>palette.text.primary;
            typo = (typography)=>typography.fontWeightNormal;
        }
        if(props.error) { status =  (palette)=>palette.error.main; }*/
    const style = {
        fontWeight: theme.typography.fontWeightLight,
        color: theme.palette.text.disabled
    };
    if (props.hasContent) {
        style.fontWeight = theme.typography.fontWeightRegular;
        style.color = '#000';//theme.palette.text.primary;
    }
    if (props.error) {
        style.color = theme.palette.error.main;
    }
    return <Tab key={props.value} value={props.value} onClick={(evt) => props.changeTab(evt, props.value)} label={props.label} style={style} />;
}

// eslint-disable-next-line react/display-name
export const CampaignForm = ({ diaForm, tabErrors, waiting }) => {
    const { transientValues, setTransient, initialValues, values, updateInitialValues, errors, setValue, handleSubmit, onChange, isDirty, status } = diaForm;
    const tabContainer = useRef<HTMLDivElement>(null);
    const id = values.id;
    const [isDuplicate, setIsDuplicate] = useState(false);
    const isNew = (id === 'new' || isDuplicate);
    const [showEContract, setShowEcontract] = useState(false);
    const [showChallengeDialog, setShowChallengeDialog] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { id: urlId } = useParams();
    const toolbarContext = useContext(ToolbarContext);
    // The tab SET follows the campaign's own type (spec: "Tabs follow the campaign's type,
    // not the URL route"). While values.type is empty (e.g. a legacy row with no type), the
    // route is the source of truth. baseUrl stays route-driven so tab links navigate within
    // the route tree the editor was opened from.
    const isCreativeStudioRoute = location.pathname.startsWith('/cs-campaigns');
    const baseUrl = isCreativeStudioRoute ? '/cs-campaigns/' : '/campaigns/';
    const isCreativeStudio = (values?.type || (isCreativeStudioRoute ? 'creative_studio' : 'standard')) === 'creative_studio';
    const [tab, setTab] = useState('');
    const [tabHeight, setTabHeight] = useState(480);
    const { toast, dismiss } = useToast();
    const daybook = useDaybookContext();
    useEffect(() => {
        if (id) {
            setIsDuplicate(false);
        }
    }, [id]);
    const changeTab = useCallback((evt, newTab) => {
        if (status != 'ready') return;
        navigate(baseUrl + (isNew ? 'new' : values.job_reference) + '/' + newTab + (location.search ? location.search : ''), { replace: true });
    }, [location.search, id, status, baseUrl]);
    const [currencyObj, setCurrencyObj] = useState<{ currencyGlyph?: any; defaultCurrency?: any; options: any[] }>({
        currencyGlyph: values?.currency ? values.currency.glyph : '£',
        options: values?.currencies ? [...values.currencies] : []
    });
    const userContext = useUserContext();
    const inProductionLocked = isInProductionLocked(
        values?.status,
        values?.type || (isCreativeStudioRoute ? 'creative_studio' : 'standard'),
        userContext.hasPermission('campaigns.in_production_edit')
    );
    React.useEffect(() => {
        setCurrencyObj({
            defaultCurrency: values.currency ? Object.assign({}, values.currency) : null,
            options: values.currencies ? [...values.currencies] : []
        });
    }, [values.currency, values.currencies]);

    useEffect(() => {
        if (!tabContainer?.current) {
            return;
        }
        const h = tabContainer?.current?.clientHeight;
        setTabHeight(h);
    }, [tabContainer.current]);

    useEffect(() => {
        if (values?.is_locked && !userContext.hasPermission('campaigns.ignore_lock')) {
            toast(<span>This campaign is locked and cannot be edited. <br />Please contact distribution.</span>, { variant: 'warning' });
        } else if (inProductionLocked) {
            toast(<span>This campaign is in production and cannot be edited by you.</span>, { variant: 'warning' });
        }
    }, [values?.is_locked, inProductionLocked]);

    const readOnly = !userContext.hasPermission('campaigns.edit') || inProductionLocked || (values?.is_locked && !userContext.hasPermission('campaigns.ignore_lock'));
    const doSave = () => {
        console.log('CampaignForm doSave?');
        handleSubmit();
    };
    const doneEContract = () => {
        setShowEcontract(false);
    }
    const addToShortcuts = () => {
        userContext.addToShortcuts(values.id);
    };
    const removeFromShortcuts = () => {
        userContext.removeFromShortcuts(values.id);
    };
    const eContract = () => {
        setShowEcontract(true);
    }
    const createDuplicate = async () => {
        setIsDuplicate(true);
    };
    const toggleCampaignLock = () => {
        const locking = !values?.is_locked;
        setValue('is_locked', locking);
        setValue('manually_unlocked', !locking); // if locked, manually_unlocked is false

        toast(
            locking ? 'Campaign locked' : 'Campaign unlocked',
            { variant: 'info' }
        );
    };
    /*const generateChallengeEmail = useCallback(() => {
        setShowChallengeDialog(true);
    },[]);
    const doneChallenge = () => {
        setShowChallengeDialog(false);
    }*/
    useEffect(() => {
        console.log('SETTOOLBAR', 'UE1', isNew, diaForm.status, waiting);
        const buttons = [];
        let shortListed = false;
        if (!waiting) {
            if (!isNew) {
                userContext.user.campaignShortcuts.forEach((val) => {
                    if (id === val.id) {
                        shortListed = true;
                    }
                });
                if (userContext.hasPermission('campaigns.create_signature')) {
                    buttons.push({
                        type: 'button',
                        icon: <Icon icon="wpf:signature" fontSize={20} />,
                        tooltip: 'Send E-Contract for signature',
                        callback: eContract,
                        label: 'Send E-Contract for signature'
                    });
                }
                if (userContext.hasPermission('campaigns.lock_unlock')) {
                    buttons.push({
                        type: 'button',
                        icon: values.is_locked
                            ? <Icon icon="mdi:lock" fontSize={20} />
                            : <Icon icon="mdi:lock-open-variant" fontSize={20} />,
                        tooltip: values.is_locked ? 'Unlock Campaign' : 'Lock Campaign',
                        callback: toggleCampaignLock,
                        label: values.is_locked ? 'Unlock Campaign' : 'Lock Campaign'
                    });
                }
                /*buttons.push({
                    type: 'button',
                    icon: <Icon icon="fluent:mail-list-20-regular" fontSize={20}/>,
                    tooltip: 'Send Challenge email',
                    callback: generateChallengeEmail,
                    label: 'Send Challenge email'
                });*/
                if (shortListed) {
                    buttons.push({
                        type: 'button',
                        icon: <BookmarkRemove />,
                        tooltip: 'Remove from Shortcuts',
                        callback: removeFromShortcuts,
                        label: 'Remove from Shortcuts'
                    });
                } else {
                    buttons.push({
                        type: 'button',
                        icon: <BookmarkAdd />,
                        tooltip: 'Add to Shortcuts',
                        callback: addToShortcuts,
                        label: 'Add to Shortcuts'
                    });
                }
            }
            if (!readOnly) {
                buttons.push({
                    type: 'button',
                    icon: 'save',
                    tooltip: 'Save',
                    callback: doSave,
                    label: 'Save',
                    style: 'primary',
                    disabled: !isDirty
                });
                buttons.push({
                    type: 'button',
                    icon: 'duplicate',
                    tooltip: 'Create Duplicate',
                    label: 'Clone Campaign',
                    style: 'secondary',
                    callback: createDuplicate,
                    className: isDirty ? 'intent-primary' : 'intent-warning',
                    disabled: isDirty || isDuplicate
                });
            }
        }

        console.log('SET secondary', values.job_reference);
        toolbarContext.setSecondary({
            primaryUrl: baseUrl,
            title: (!isNew ? '' + values.job_reference + ' / ' + values.name : 'New Campaign'),
            secondary: buttons,
            secondaryUrl: isNew ? 'new' : values.job_reference
        });
    }, [id, waiting, isDirty, isDuplicate, diaForm.values.name, diaForm.status, userContext.user.campaignShortcuts, values.is_locked]);
    const hookSetTab = useCallback((v) => {
        setTab(v);
    }, []);
    useEffect(() => {
        if (isDuplicate && !isNaN(values?.id)) {
            const changes = new GQLChanges(fieldMappings);
            // clone() returns a deep copy of the form value object.
            const newValues = changes.clone(values) as Record<string, unknown>;
            newValues.name = `${newValues.name} (copy)`;
            newValues.panelBookings = [];
            newValues.status = "Draft";
            delete newValues.is_locked;
            delete newValues.manually_unlocked;
            delete newValues.photoPaidTotal;
            delete newValues?.invoice;
            newValues.authorizationFiles = [];
            newValues.popsFiles = [];
            newValues.artworkFiles = [];
            delete newValues?.externalPortfolioUrl;
            updateInitialValues({}, null, true);
            setValue('', newValues);
        }
    }, [isDuplicate, isNew, values?.id]);
    const isReadOnly = useCallback((hasPriceImpact) => {
        console.log('readonlycheck', hasPriceImpact, diaForm?.initialValues?.status);
        return readOnly ? readOnly : (hasPriceImpact && diaForm?.initialValues?.status == 'Quoted');
    }, [readOnly, diaForm?.initialValues?.status]);

    if (status != 'ready' && !diaForm.values.id) {
        return <WaitSpinner variant="panel" />;
    }

    const contextValue: CampaignOutletContextValue = {
        setValue: setValue, onChange: onChange, errors: errors, loading: status == 'loading', readOnly: readOnly, inProductionLocked: inProductionLocked,
        updateInitialValues: updateInitialValues, jobRef: values?.job_reference, setTab: hookSetTab,
        values: values, initialValues: initialValues, isDirty, isReadOnly,
        transientValues: transientValues,
        setTransient: setTransient
    };



    console.log('CAMPAIGNFORM', { ref: values.job_reference, id: urlId, status: diaForm.status });

    // MUI Tabs only tracks Tabs that are direct children (it flattens arrays but not
    // Fragments — Tabs inside a Fragment never receive `selected`/the indicator),
    // so the tab list is built as a flat array.
    const tabs: JSX.Element[] = [];
    tabs.push(<TabStatus value="overview" changeTab={changeTab} label="Overview" hasContent={true} error={false} />);
    tabs.push(<TabStatus value="summary" changeTab={changeTab} label="Summary" hasContent={true} error={tabErrors?.summary} />);
    if (userContext.hasPermission('campaigns.panel_bookings')) {
        tabs.push(<TabStatus value="panel-bookings" changeTab={changeTab} label="Panel Bookings" hasContent={!!values?.panelBookings} error={tabErrors?.panelBookings} />);
    }
    if (isCreativeStudio) {
        tabs.push(<TabStatus value="creative" changeTab={changeTab} label="Creative Studios" hasContent={true} error={false} />);
        tabs.push(<TabStatus value="contract" changeTab={changeTab} label="Contract" hasContent={true} error={tabErrors?.areaBookings} />);
        if (!isNew) {
            tabs.push(<TabStatus value="changes" changeTab={changeTab} hasContent={true} error={tabErrors?.changes} label="Changes" />);
        }
    } else {
        if (userContext.hasPermission('digitalbookings.view')) {
            tabs.push(<TabStatus value="digital-bookings" changeTab={changeTab} label="Digital Bookings" hasContent={values?.digitalBookings && values.digitalBookings.length} error={tabErrors?.DigitalBookings} />);
        }
        tabs.push(<TabStatus value="area-bookings" changeTab={changeTab} label="Area Bookings" hasContent={values?.areaBookings && values.areaBookings.length} error={tabErrors?.areaBookings} />);
        if (daybook?.useCreativeStudio) {
            if (userContext.hasPermission('campaigns.experiential') && values?.experientials && values.experientials.length > 0) {
                tabs.push(<TabStatus value="experientials" changeTab={changeTab} hasContent={true} error={tabErrors?.experientials} label="Experiential" />);
            }
            if (userContext.hasPermission('campaigns.ambient') && values?.ambients && values.ambients.length > 0) {
                tabs.push(<TabStatus value="ambients" changeTab={changeTab} hasContent={true} error={tabErrors?.ambients} label="Ambient" />);
            }
            if (userContext.hasPermission('campaigns.creative_studio')) {
                tabs.push(<TabStatus value="creative-studio" changeTab={changeTab} hasContent={values?.creativeStudio && values.creativeStudio.length} error={tabErrors?.creativeStudio} label='Creative Studio' />);
            }
        } else {
            if (userContext.hasPermission('campaigns.experiential')) {
                tabs.push(<TabStatus value="experientials" changeTab={changeTab} hasContent={true} error={tabErrors?.experientials} label="Experiential" />);
            }
            if (userContext.hasPermission('campaigns.ambient')) {
                tabs.push(<TabStatus value="ambients" changeTab={changeTab} hasContent={true} error={tabErrors?.ambients} label="Ambient" />);
            }
        }
        if (userContext.hasPermission('campaigns.media_production')) {
            tabs.push(<TabStatus value="media-productions" changeTab={changeTab} hasContent={values?.mediaProductions && values.mediaProductions.length} error={tabErrors?.mediaProductions} label='Media Production' />);
        }
        if (userContext.hasPermission('campaigns.other')) {
            tabs.push(<TabStatus value="sundries" changeTab={changeTab} hasContent={values?.sundries && values.sundries.length} error={tabErrors?.sundries} label='Other' />);
        }
        tabs.push(<TabStatus value="portfolio" changeTab={changeTab} hasContent={true} error={tabErrors?.portfolio} label="Proposal" />);
        if (userContext.hasPermission('campaigns.costs') || userContext.hasPermission('campaigns.costs_view')) {
            tabs.push(<TabStatus value="costs" changeTab={changeTab} hasContent={values?.costs && values.costs.length} error={tabErrors?.costs} label='Costs' />);
        }
        if (values.clientCompanyContact?.name === 'Edinburgh website sales') {
            tabs.push(<TabStatus value="epos" changeTab={changeTab} hasContent={values.epos_artwork_reviewed || (values.popsFiles?.length > 0)} error={tabErrors?.costs} label='EPOS' />);
        }
        if (!isNew) {
            tabs.push(<TabStatus value="changes" changeTab={changeTab} hasContent={true} error={tabErrors?.changes} label="Changes" />);
        }
    }

    const tabValues = tabs.map(t => t.props.value);
    const activeTab = tabValues.indexOf(tab) !== -1 ? tab : 'summary';

    return (
        <div style={{ "--tab-height": tabHeight + 'px' } as React.CSSProperties}>
            <ExchangeRatesContextProvider currencyId={values?.currency?.id} options={currencyObj?.options} fixedExchangeRates={values.campaignExchangeRates}>
                {(diaForm.status != 'ready' || (values.job_reference != urlId && !isNew)) &&
                    <WaitSpinner variant="overlay" />
                }
                <EContractDialog isDirty={isDirty} status={values.status} isShown={showEContract} done={doneEContract} reference={values.job_reference} portfolio_hash={values.portfolio_hash}
                    hasContactEmail={!!values.clientCompanyContact?.email}
                />
                {/*
                <ChallengeEmailDialog isShown={showChallengeDialog} done={doneChallenge} reference={values.job_reference}
                />
            */}
                <Box ref={tabContainer} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={changeTab}>
                        {tabs}
                    </Tabs>
                </Box>
                <Outlet context={contextValue} />

            </ExchangeRatesContextProvider>
        </div>
    );
}
